import { CosmosRuntimeState } from 'providers/contexts/CosmosRuntimeContext';
import { getRoute } from 'utility/functions';

export interface BreadcrumbState {
  path: string;
  label: string;
}

type TranslateFunction = (key: string, defaultValue: string) => string;

// Returns the breadcrumb state based on the application and account type if provided.
export function getBreadcrumb(
  application: string,
  accountType: string | undefined,
  path?: string,
  t?: TranslateFunction,
): BreadcrumbState {
  const usingAdminUserCtx = accountType === 'admin';
  const isTemplatesPath = path?.includes('/templates');
  const translate = t ?? ((key: string, defaultValue: string) => defaultValue);
  switch (application) {
    case 'editorial':
      return {
        path: path || '/editorial/search',
        label: path
          ? isTemplatesPath
            ? 'Back to Templates'
            : translate('blox.search.actions.returnAlt', 'Back to search results')
          : 'Editorial Search',
      };
    case 'user':
      return {
        path:
          path ||
          (usingAdminUserCtx ? '/settings/admins/manage' : '/user/account/search'),
        label: path
          ? translate('blox.search.actions.returnAlt', 'Back to search results')
          : usingAdminUserCtx
            ? translate('blox.navigation.user.admin_management', 'Admin Management')
            : 'User Search',
      };
    case 'feeds':
      return {
        path: path || '/feeds/search',
        label: path
          ? translate('blox.search.actions.returnAlt', 'Back to search results')
          : 'Feed Search',
      };
    case 'services':
      return {
        path: '/services/search',
        label: path
          ? translate('blox.search.actions.returnAlt', 'Back to search results')
          : translate('blox.services.breadcrumbs.search', 'Services Search'),
      };
    case 'transactions':
      return {
        path: path || '/transactions/search',
        label: path
          ? translate(
              'blox.transactions.breadcrumbs.backToSearch',
              'Back to search results',
            )
          : translate('blox.transactions.breadcrumbs.search', 'Transactions Search'),
      };
    case 'settings-admin':
      return {
        path: path || '/settings/admins/manage',
        label: path
          ? translate('blox.search.actions.returnAlt', 'Back to search results')
          : 'Manage admins',
      };
    case 'settings-groups':
      return {
        path: path || '/settings/admins/groups',
        label: path
          ? translate('blox.search.actions.returnAlt', 'Back to search results')
          : 'Manage groups',
      };
    default:
      return {
        path: '',
        label: '',
      };
  }
}

export function getBackToSearchLink(
  application: string,
  history: CosmosRuntimeState['history'],
): string {
  // For admin settings routes (admins or groups), check for management paths
  const isAdminManagement =
    application === 'settings-admin' || application === 'settings-groups';

  // Match `/application/search`, `/application/*/search`, or `/application/search/templates`
  // OR for admin management, match `/settings/admins/manage` or `/settings/admins/groups`
  // For admin management, only match the exact list/manage routes (avoid matching edit subpaths)
  const regex = isAdminManagement
    ? /\/settings\/admins\/(?:manage|groups)(?:\/?$)/
    : new RegExp(`/${application}(?:/[^/]+)?/search(?:/templates)?/?$`);
  for (let i = history.length - 1; i >= 0; i--) {
    const { pathname, search } = history[i];
    // Return combined path and search
    if (regex.test(pathname)) {
      return `${getRoute(pathname)}${search?.trim() || ''}`;
    }
  }
  // If no history match found:
  // - for groups, fall back to the groups list
  // - otherwise return empty string to allow getBreadcrumb to provide a fallback
  return application === 'settings-groups' ? '/settings/admins/groups' : '';
}

// Returns the path to use for search or breadcrumb navigation
export function getSearchOrBreadcrumbPath(
  application: string,
  accountType: string | undefined,
  history: CosmosRuntimeState['history'],
): string {
  const backToSearch = getBackToSearchLink(application, history);
  const fallback = getBreadcrumb(application, accountType, backToSearch).path;
  return backToSearch || fallback;
}
